import { useEffect, useRef } from 'react';
import { ChevronDown } from 'lucide-react';
import { DIAGNOSTIC_FACTS } from '@/lib/marketingCtas';

/**
 * Straight Answers (spec 010 §4 E, §5.4). Plain and honest. Open list from md up, accordion at
 * phone width (prerendered open so the answers are in the HTML; collapsed on hydrate at <768px).
 */

// TODO(owner): "Is the course free forever?" is a pricing-policy statement and needs the owner's
// decision (GitHub issue #307). This is the neutral interim copy from spec 010 §5.4. Do not
// reword it into a stronger promise until the owner decides; it is flagged in the PR.
export const FREE_FOREVER_ANSWER =
  "Yes for the first session and the course map; we'll say so plainly here before anything changes.";

export const SAT_ABOUT_ANSWERS = [
  {
    q: 'Do I need an account?',
    a: 'No. You can take the diagnostic and practice without one. Nothing is saved. Create a free account to keep your progress.',
  },
  { q: 'Is the course free forever?', a: FREE_FOREVER_ANSWER },
  {
    q: 'How long is the diagnostic?',
    a: `${DIAGNOSTIC_FACTS.questions} questions, ${DIAGNOSTIC_FACTS.minutesLabel}, across all eight areas.`,
  },
  {
    q: 'Are these real SAT questions?',
    a: "They're written to the digital SAT's format and skills. Full-length practice tests run in College Board's Bluebook; you enter your score.",
  },
  {
    q: 'What does a tutor add?',
    a: 'A person who can start from your results and plan sessions around them. Tutoring is paid and optional, booked and paid through Mentara.',
  },
] as const;

export function SatAboutAnswers() {
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function' || !window.matchMedia('(max-width: 767px)').matches) return;
    listRef.current?.querySelectorAll('details').forEach((d) => d.removeAttribute('open'));
  }, []);

  return (
    <section aria-labelledby="answers-title" className="mx-auto w-full max-w-6xl px-4 py-14 sm:px-6 lg:py-20">
      <h2 id="answers-title" className="text-3xl font-bold tracking-tight sm:text-4xl">
        Straight answers
      </h2>
      <div ref={listRef} className="mt-8 grid gap-x-14 gap-y-2 md:grid-cols-2 md:gap-y-7" data-testid="straight-answers">
        {SAT_ABOUT_ANSWERS.map(({ q, a }) => (
          <details key={q} open className="group border-b py-1 md:border-b-0 md:py-0">
            <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between gap-3 rounded text-base font-semibold outline-none marker:hidden focus-visible:ring-2 focus-visible:ring-primary md:pointer-events-none md:min-h-0 [&::-webkit-details-marker]:hidden">
              {q}
              <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180 md:hidden" aria-hidden />
            </summary>
            <p className="max-w-prose pb-3 pt-1 text-[15px] leading-relaxed text-muted-foreground md:pb-0">{a}</p>
          </details>
        ))}
      </div>
    </section>
  );
}
